/**
 * PasoEquipos — Paso 2 del IE. Array editable de equipos; item autonumerado 1-based.
 */
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/ui/button.js";
import type { IeEquipo } from "../../types.js";
import { LabeledField, labelClass } from "./fields.js";

/** Reasigna item de forma 1-based tras agregar/quitar filas. */
function renumerar(equipos: IeEquipo[]): IeEquipo[] {
  return equipos.map((e, i) => ({ ...e, item: i + 1 }));
}

export function PasoEquipos(props: {
  value: IeEquipo[];
  onChange: (next: IeEquipo[]) => void;
  disabled?: boolean;
}) {
  const { value, onChange, disabled } = props;

  function agregar() {
    onChange(
      renumerar([
        ...value,
        { item: 0, equipo: "", marca: "", modelo: "", serie: "", designacion: "" },
      ]),
    );
  }

  function quitar(index: number) {
    onChange(renumerar(value.filter((_, i) => i !== index)));
  }

  function set(index: number, key: keyof IeEquipo, v: string) {
    onChange(value.map((e, i) => (i === index ? { ...e, [key]: v } : e)));
  }

  return (
    <div className="space-y-4">
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No hay equipos todavía. Agrega el primero con el botón de abajo.
        </p>
      ) : (
        <ul className="space-y-4">
          {value.map((equipo, i) => (
            <li key={i} className="rounded-lg border border-border bg-background/60 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className={labelClass}>Equipo {equipo.item}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => quitar(i)}
                  className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em] text-destructive"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  Quitar
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabeledField
                  id={`eq-${i}-equipo`}
                  label="Equipo"
                  value={equipo.equipo}
                  onChange={(v) => set(i, "equipo", v)}
                  disabled={disabled}
                  placeholder="Camión"
                />
                <LabeledField
                  id={`eq-${i}-marca`}
                  label="Marca"
                  value={equipo.marca}
                  onChange={(v) => set(i, "marca", v)}
                  disabled={disabled}
                  placeholder="CAT"
                />
                <LabeledField
                  id={`eq-${i}-modelo`}
                  label="Modelo"
                  value={equipo.modelo}
                  onChange={(v) => set(i, "modelo", v)}
                  disabled={disabled}
                  placeholder="785C"
                />
                <LabeledField
                  id={`eq-${i}-serie`}
                  label="Serie"
                  value={equipo.serie}
                  onChange={(v) => set(i, "serie", v)}
                  disabled={disabled}
                  placeholder="N° de serie"
                />
                <LabeledField
                  id={`eq-${i}-designacion`}
                  label="Designación / TAG"
                  value={equipo.designacion}
                  onChange={(v) => set(i, "designacion", v)}
                  disabled={disabled}
                  placeholder="TAG"
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={agregar}
        className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
      >
        <Plus className="size-3.5" aria-hidden />
        Agregar equipo
      </Button>
    </div>
  );
}
